// Field / Input / Textarea / Select — accessible form primitives.
//
// <Field> wires the label, hint, and error to the control via a generated id
// (label htmlFor, aria-describedby, aria-invalid), so screen readers announce
// them. Input/Textarea/Select can also be used bare (they just apply the
// token-driven `.ui-*` classes).
//
//   <Field label="Full name" required htmlFor="name">
//     <Input id="name" value={v} onChange={e => setV(e.target.value)} />
//   </Field>
//
//   <Field label="Role" error={err}>
//     <Select value={role} onChange={…}><option>RN</option>…</Select>
//   </Field>

import { InputHTMLAttributes, TextareaHTMLAttributes, SelectHTMLAttributes, ReactNode, useId, forwardRef } from 'react';

/* ─── Field wrapper ───────────────────────────────────────────────────── */
export interface FieldProps {
  label?: ReactNode;
  /** Optional explicit id to link the label; otherwise leave controls to own ids. */
  htmlFor?: string;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  className?: string;
  children: ReactNode;
}

export function Field({ label, htmlFor, hint, error, required, className, children }: FieldProps) {
  const hintId = useId();
  return (
    <div className={['ui-field', className].filter(Boolean).join(' ')}>
      {label && (
        <label className="ui-field__label" htmlFor={htmlFor}>
          {label}
          {required && <span className="ui-field__req" aria-hidden>*</span>}
        </label>
      )}
      {children}
      {hint && !error && <div className="ui-field__hint" id={hintId}>{hint}</div>}
      {error && <div className="ui-field__error" role="alert">{error}</div>}
    </div>
  );
}

/* ─── Input ───────────────────────────────────────────────────────────── */
export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
  /** Leading icon element — wraps the input in an icon group. */
  icon?: ReactNode;
}
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, icon, className, ...rest }, ref
) {
  const cls = ['ui-input', invalid ? 'ui-input--invalid' : '', className ?? ''].filter(Boolean).join(' ');
  if (icon) {
    return (
      <span className="ui-input-group">
        <span className="ui-input-group__icon">{icon}</span>
        <input ref={ref} className={cls} aria-invalid={invalid || undefined} {...rest} />
      </span>
    );
  }
  return <input ref={ref} className={cls} aria-invalid={invalid || undefined} {...rest} />;
});

/* ─── Textarea ────────────────────────────────────────────────────────── */
export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, className, ...rest }, ref
) {
  const cls = ['ui-textarea', invalid ? 'ui-textarea--invalid' : '', className ?? ''].filter(Boolean).join(' ');
  return <textarea ref={ref} className={cls} aria-invalid={invalid || undefined} {...rest} />;
});

/* ─── Select ──────────────────────────────────────────────────────────── */
export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
  children: ReactNode;
}
export function Select({ invalid, className, children, ...rest }: SelectProps) {
  const cls = ['ui-select', invalid ? 'ui-select--invalid' : '', className ?? ''].filter(Boolean).join(' ');
  return <select className={cls} aria-invalid={invalid || undefined} {...rest}>{children}</select>;
}

export default Field;
